/**
 * gRPC on the API tab: the methods the selected service declares in its .proto files, called with a JSON message
 * (vfs-service src/apitester/grpc.js: encoded from the .proto files, sent as gRPC-Web through the preview runner, which
 * speaks gRPC over HTTP/2 to the service), on its main port or one of its named ports. The answer: the gRPC status, its
 * message, the response as JSON and the time. Client-streaming methods are listed but cannot be called; a viewer reads
 * only. Nothing shows for a service without .proto files.
 */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getGrpcMethods, sendGrpc } from '../../services/ApiTesterService';

const pretty = (value) => JSON.stringify(value, null, 2);

export default function GrpcCard({ projectId, service, canWrite = false }) {
  const [found, setFound] = useState(null);
  const [methodPath, setMethodPath] = useState('');
  const [message, setMessage] = useState('{}');
  const [port, setPort] = useState('');
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let live = true;
    setFound(null); setAnswer(null); setError(null);
    if (!service) { return undefined; }
    Promise.resolve(getGrpcMethods(projectId, service)).then((data) => { if (live) { setFound(data || { services: [] }); } }).catch(() => { if (live) { setFound({ services: [] }); } });
    return () => { live = false; };
  }, [projectId, service]);

  const methods = useMemo(() => (found?.services || []).flatMap((svc) => svc.methods.map((m) => ({ ...m, service: svc.name }))), [found]);
  const method = methods.find((m) => m.path === methodPath) || null;
  useEffect(() => {
    if (!methods.length) { return; }
    const first = methods.find((m) => !m.clientStreaming) || methods[0];
    setMethodPath(first.path);
    setMessage(pretty(first.example || {}));
  }, [methods]);

  if (!methods.length) { return null; }

  const pick = (event) => {
    const next = methods.find((m) => m.path === event.target.value);
    setMethodPath(event.target.value);
    setMessage(pretty(next?.example || {}));
    setAnswer(null); setError(null);
  };
  const call = async() => {
    setError(null); setAnswer(null);
    let parsed;
    try { parsed = JSON.parse(message || '{}'); } catch { setError('The message is not valid JSON.'); return; }
    setSending(true);
    try {
      setAnswer(await sendGrpc(projectId, { service, method: methodPath, message: parsed, ...(port.trim() ? { port: port.trim() } : {}) }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };
  const blocked = !canWrite || method?.clientStreaming;

  return (
    <section className="api-card" data-testid="api-grpc">
      <h3>gRPC</h3>
      <p className="api-muted">The methods of this service&apos;s .proto files, called with a JSON message.</p>
      <label>
        Method{' '}
        <select data-testid="api-grpc-method" onChange={pick} value={methodPath}>
          {methods.map((m) => (
            <option key={m.path} value={m.path}>{m.path}{m.serverStreaming ? ' (stream)' : ''}{m.clientStreaming ? ' (client stream: not callable)' : ''}</option>
          ))}
        </select>
      </label>{' '}
      <label>
        Port{' '}
        <input data-testid="api-grpc-port" onChange={(event) => setPort(event.target.value)} placeholder="main (or a port name)" value={port} />
      </label>
      <textarea aria-label="Message (JSON)" data-testid="api-grpc-message" onChange={(event) => setMessage(event.target.value)} rows={6} style={{ width: '100%', fontFamily: 'monospace' }} value={message} />
      <button className="api-send" data-testid="api-grpc-send" disabled={blocked || sending} onClick={call} title={!canWrite ? 'Viewers cannot call gRPC methods' : undefined} type="button">
        {sending ? 'Calling…' : 'Call'}
      </button>
      {error && <p className="api-error" role="alert">{error}</p>}
      {answer && (
        <div data-testid="api-grpc-answer">
          <p>
            <strong className={answer.code === 0 ? 'api-ok' : 'api-bad'}>{answer.codeName}</strong>
            {answer.message && <> · {answer.message}</>} · {answer.ms} ms
          </p>
          <pre>{pretty(answer.response ?? answer.responses)}</pre>
        </div>
      )}
    </section>
  );
}

GrpcCard.propTypes = {
  canWrite: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
  // The selected service (`services/<name>`).
  service: PropTypes.string,
};
